import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Media from 'react-media';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	withTheme,
	useMediaQuery,
	Typography,
	MenuItem,
	Divider
} from '@material-ui/core';

import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import {SmallerTabsV2, SmallerTabV2} from "./SmallerTabs";
import PatternsTab from "../containers/PatternsTab";

const styles = makeStyles(theme => ({
	navItems: {
		backgroundColor: theme.palette.common.white
	},

	tab: {
		fontSize: 16,
	},

	patterns: {
		marginLeft: theme.spacing(.625),
	},

	sectionTitle: {
		fontSize: '0.9rem',
		fontWeight: 'bold',
		textTransform: 'uppercase',
		letterSpacing: theme.spacing(.5),
		marginLeft: theme.spacing(3),
		paddingTop: "1rem",
		marginBlockEnd: "1em",
		marginInlineStart: theme.spacing(3),
		marginInlineEnd: "0px"
	},

	iconContainer: {
		width: theme.spacing(4)
	},

	menuItem: {
		borderLeftWidth: theme.spacing(.5),
		borderLeftColor: 'transparent',
		borderLeftStyle: 'solid',

		[theme.breakpoints.up('lg')]: {
			paddingLeft: theme.spacing(3)
		},

		"@media (max-width: 1023px)": {
			fontSize: "1rem"
		},

		// Overriding links style - probably want to do globably - unrelated to material UI
		textDecoration: 'none',
		'&:focus': {
			outlineWidth: '0px'
		}
	},

	menuItemActive: {
		borderLeftWidth: theme.spacing(.5),
		borderLeftColor: theme.palette.primary.main,
		borderLeftStyle: 'solid'
	},

	menuIconActive: {
		color: theme.palette.primary.main
	}
}));

function NavigationItems(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);
	const hasDiscussions = AuthorizedUser.hasDiscussions(authenticatedUser);
	const hasInbox = AuthorizedUser.hasInbox(authenticatedUser);
	const [selectedTab, setSelectedTab] = useState(hasInbox ? "signals" : hasDiscussions ? "trends" : false);
	const isXs = useMediaQuery(props.theme.breakpoints.down("xs"));

	let classes = styles(),
		{theme, installable, handleInstallBtn} = props;

	const tabClicked = (event, item) => {
		setSelectedTab(item);
		props.history.push(items[item.toLowerCase()].path);
	};

	let patternsLabel = "Patterns";
	const items = {
		signals: {
			topLevel: true,
			label: "Signals",
			path: "/inbox",
			icon: ["fas", "wifi"],
			iconTransform: {rotate: 90},
			activeMatcher: new RegExp(/^(\/$|\/inbox)/, "ig"),
			hasAccess: hasInbox
		},
		patterns: {
			topLevel: true,
			label: patternsLabel,
			path: `/${patternsLabel.toLowerCase()}`,
			icon: ["fad", "braille"],
			hasAccess: AuthorizedUser.hasPatterns(authenticatedUser),
			renderCmp: <PatternsTab
				tabIndex={0}
				key={patternsLabel.toLowerCase()}
				value={patternsLabel.toLowerCase()}
				label={patternsLabel}
				className={`${classes.tab} ${classes.patterns}`}
				clickHandler={tabClicked}
				history={props.history} />
		},
		trends: {
			topLevel: true,
			label: "Trends",
			path: "/trends",
			icon: ["fal", "comments-alt"],
			hasAccess: hasDiscussions
		},
		reports: {
			topLevel: true,
			label: "Reports",
			path: "/reports",
			icon: ["fas", "th"],
			hasAccess: AuthorizedUser.hasReports(authenticatedUser)
		},
		settings: {
			mobileOnly: true,
			hasAccess: true,
			label: "Settings",
			path: "/settings",
			icon: ["far", "sliders-h"]
		}
	};

	useEffect(() => {
		if (!isXs) {
			let topLevelItem,
				keys = Object.keys(items);

			for (let item of keys) {
				let obj = items[item],
					pathName = props.location.pathname,
					pathIndex = pathName.indexOf(obj.path),
					conditional = !obj.mobileOnly && pathIndex > -1 ||
						(obj.topLevel && (pathIndex > -1 || obj.activeMatcher && obj.activeMatcher.test(pathName)));

				if (conditional) {
					topLevelItem = items[item];
					break;
				}
			}

			if (topLevelItem && topLevelItem.hasAccess) {
				setSelectedTab(topLevelItem.label.toLowerCase());
			} else {
				setSelectedTab(false);
			}
		}
	}, [items, props.location.pathname, setSelectedTab, isXs]);

	const isActive = (item) => {
		let matches = false,
			{location} = props,
			matcher = item.activeMatcher || item.path,
			isRegEx = matcher instanceof RegExp,
			fullPath = `${location.pathname}${location.search}`;

		if (isRegEx) {
			matches = fullPath.match(matcher);
		} else {
			matches = fullPath.indexOf(matcher) > -1;
		}

		return matches;
	};

	const getMenuItem = (item) => {		
		return (
			item.hasAccess ?
				<MenuItem
					tabIndex={0}
					component={Link}
					onClick={close}
					to={item.path}
					className={`${isActive(item) ? classes.menuItemActive : ''} ${classes.menuItem}`}>
					{item.icon ?
						<span className={classes.iconContainer}>
							<FontAwesomeIcon
								title={item.label}
								icon={item.icon}
								transform={item.iconTransform}
								className={isActive(items) ? classes.menuIconActive : ''} />
						</span> : null
					} {item.label}
				</MenuItem> : null
		);
	};

	const close = () => {
		let {closeHandler} = props;
		if (closeHandler) {
			closeHandler();
		}
	};

	return (
		<Media query={{minWidth: theme.breakpoints.values.sm}}>
			{matches =>
				matches ?
					<SmallerTabsV2
						value={selectedTab}
						indicatorColor="secondary"
						onChange={tabClicked}>
						{Object.keys(items).map(item => {
							let obj = items[item],
								label = obj.label;

							return !obj.mobileOnly && obj.hasAccess ?
								obj.renderCmp && React.isValidElement(obj.renderCmp) ?
									obj.renderCmp
									:
									<SmallerTabV2
										key={label}
										label={label}
										value={label.toLowerCase()}
										className={classes.tab}
										tabIndex={0} />
								: null;
						})}
					</SmallerTabsV2>
					:
					<div className={classes.navItems}>
						<Typography variant="h2" className={classes.sectionTitle}>Activity</Typography>
						{getMenuItem(items.signals)}
						{/* TODO: support react components like above */}
						{/*{getMenuItem(items.patterns)}*/}
						{getMenuItem(items.trends)}
						{getMenuItem(items.reports)}
						<Divider />
						{getMenuItem(items.settings)}
						{installable ?
							<MenuItem
								className={classes.menuItem}
								onClick={() => {close(); handleInstallBtn();}}>
								<span className={classes.iconContainer}>
									<FontAwesomeIcon
										title="install app"
										icon={["far", "cloud-download-alt"]} />
								</span>	Install App
							</MenuItem> : null}
					</div>
			}
		</Media>
	);
}

NavigationItems.propTypes = {
	theme: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	history: PropTypes.object.isRequired,
	closeHandler: PropTypes.func,
	installable: PropTypes.bool,
	handleInstallBtn: PropTypes.func
};

export default withTheme(NavigationItems);